import React, { useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';

import { FormGroupControl, FormGroupSelect } from '@components/Base';
import { ClaimStatuses } from '@models/Claim';
import { claimQuickUpdate } from './action';
import { selectClaimEditModal } from './selector';
import i18n from '@components/i18n';

export interface ClaimEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  claimNumber: string;
  claimableType: string;
  claimableId: number;
  currentStatus: ClaimStatuses;
  currentTotalClaimedAmount: number;
  onSuccess?: () => void;
}

interface FormValues {
  status: ClaimStatuses;
  totalClaimedAmount: number;
}

const groupProps = { className: 'mb-3' };

const validationSchema = Yup.object({
  status: Yup.string().required(i18n.t('common:required')),
  totalClaimedAmount: Yup.number().min(0, i18n.t('common:minValue', { value: 0 })).required(i18n.t('common:required')),
});

const ClaimEditModal = ({
  isOpen,
  onClose,
  claimNumber,
  claimableType,
  claimableId,
  currentStatus,
  currentTotalClaimedAmount,
  onSuccess,
}: ClaimEditModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isUpdating, lastSuccessClaimableId, lastErrorClaimableId } = useSelector(selectClaimEditModal);
  const hasTriggeredSuccess = useRef(false);

  const initialValues: FormValues = {
    status: currentStatus,
    totalClaimedAmount: currentTotalClaimedAmount,
  };

  useEffect(() => {
    if (isOpen && lastSuccessClaimableId === claimableId && !hasTriggeredSuccess.current) {
      hasTriggeredSuccess.current = true;
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    }
  }, [isOpen, lastSuccessClaimableId, claimableId, onClose, onSuccess]);

  useEffect(() => {
    if (!isOpen) {
      hasTriggeredSuccess.current = false;
    }
  }, [isOpen]);

  const handleSubmit = (values: FormValues) => {
    dispatch(
      claimQuickUpdate({
        claimableType,
        claimableId,
        status: values.status,
        totalClaimedAmount: values.totalClaimedAmount,
      }),
    );
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      aria-labelledby="claim-edit-modal-title"
      aria-describedby="claim-edit-modal-description"
      centered
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid }) => (
          <FormikForm>
            <Modal.Header closeButton>
              <Modal.Title id="claim-edit-modal-title">
                {t('claimEditModal.title', { claimNumber })}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body id="claim-edit-modal-description">
              <FormGroupSelect name="status" label={t('common:status')} groupProps={groupProps}>
                <option value={ClaimStatuses.Open}>{t('claimStatuses.open')}</option>
                <option value={ClaimStatuses.InReview}>{t('claimStatuses.inReview')}</option>
                <option value={ClaimStatuses.InProgress}>{t('claimStatuses.inProgress')}</option>
                <option value={ClaimStatuses.Approved}>{t('claimStatuses.approved')}</option>
                <option value={ClaimStatuses.SettlementOffered}>{t('claimStatuses.settlementOffered')}</option>
                <option value={ClaimStatuses.PaymentProcessing}>{t('claimStatuses.paymentProcessing')}</option>
                <option value={ClaimStatuses.Closed}>{t('claimStatuses.closed')}</option>
                <option value={ClaimStatuses.Voided}>{t('claimStatuses.voided')}</option>
              </FormGroupSelect>

              <FormGroupControl
                name="totalClaimedAmount"
                label={t('claimForm.totalClaimedAmount.label')}
                groupProps={groupProps}
                type="number"
                min="0"
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onClose} disabled={isUpdating}>
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={isUpdating || !isValid}>
                {isUpdating ? t('common:saving') : t('common:save')}
              </Button>
            </Modal.Footer>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

export { ClaimEditModal };
